import { format } from "date-fns";
import { Typography } from "@/components/atoms";
import { driverDelayCaption } from "@/helpers/bookingHelpers";
import { useAppSelector } from "@/redux/hooks";
import { transferRateKind } from "@/redux/slices/clientBooking/bookingPayload";
import {
  selectBookedForDuration,
  selectBookingExtras,
  selectBookingTransferType,
  selectBookingVehicle,
  selectFixedPrice,
  selectJobRate,
  selectNumOfBags,
  selectNumOfPassengers,
  selectPickupDatetime,
  selectPickupFlightDelayMinutes,
  selectPickupFlightNumber,
} from "@/redux/slices/clientBooking/selectors";
import { capacityLabel, extrasLabel, formatDuration } from "../vehicleTransfer";
import { DetailRow } from "./DetailRow";
import { PassengerRow } from "./PassengerRow";
import { TripVehicleRow } from "./TripVehicleRow";

/**
 * The passenger / date / vehicle rows of the Trip Details box, read live from the booking draft.
 * Shared by both Booking Summary variants (draft steps and the priced Payment step) so the section
 * reads identically everywhere. Each row renders only when its data exists — no em-dash placeholders.
 * The vehicle row carries three lines (transfer type, vehicle + capacity, extras), so it's laid out
 * inline here rather than through the two-line DetailRow.
 */
export const TripCoreRows = () => {
  const pickupDatetime = useAppSelector(selectPickupDatetime);
  const pickupDate = pickupDatetime ? new Date(pickupDatetime) : null;
  const hasValidDate = !!pickupDate && !Number.isNaN(pickupDate.getTime());
  const vehicle = useAppSelector(selectBookingVehicle);
  const transferType = useAppSelector(selectBookingTransferType);
  const numOfPassengers = useAppSelector(selectNumOfPassengers);
  const numOfBags = useAppSelector(selectNumOfBags);
  const vehicleExtras = extrasLabel(useAppSelector(selectBookingExtras));
  // Duration only applies to hourly/daily bookings. Gate on `jobRate` — the authoritative rate set in
  // every flow (create sets it from the transfer type; view/edit hydrate it from the booking) — since
  // the hydrated transferType doesn't carry `type`.
  const jobRate = useAppSelector(selectJobRate);
  const bookedForDuration = useAppSelector(selectBookedForDuration);
  const durationLabel = transferRateKind(jobRate) !== "journey" ? formatDuration(bookedForDuration) : "";
  // The operator's price override (any transfer type now) shows here when set; auto-quoted rates with no
  // override leave this null.
  const fixedPrice = useAppSelector(selectFixedPrice);
  const fixedRateLabel = fixedPrice != null ? `$${fixedPrice}` : "";
  // Hydrated from the booking on the view-only replay (viewDraft: flight_pickup_delay_minutes), so this
  // is how an existing booking surfaces the wait. Still gated on the flight number — the wait is a
  // flight concept and the payload only ever sends it alongside one.
  //
  // It rides on the date row as a trailing caption rather than in a row of its own, because the caption
  // has to be bound to the pickup time to mean anything: it says the time beside it ALREADY includes
  // the wait, which a separate row could never say.
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const delayCaption = driverDelayCaption(useAppSelector(selectPickupFlightDelayMinutes));

  return (
    <>
      <PassengerRow />
      {hasValidDate && pickupDate && (
        <DetailRow
          icon="Calendar"
          title={format(pickupDate, "d MMMM yyyy")}
          subtitle={format(pickupDate, "h:mm a")}
          subtitleTrailing={
            pickupFlightNumber && delayCaption ? (
              <Typography variant="caption" className="whitespace-nowrap text-info">
                {delayCaption}
              </Typography>
            ) : undefined
          }
        />
      )}
      {vehicle && (
        <TripVehicleRow
          transferLabel={transferType?.label ?? "Vehicle and Transfer"}
          vehicleType={`${vehicle.name} (${capacityLabel(numOfPassengers ?? 1, numOfBags ?? 0)})`}
          duration={durationLabel || undefined}
          fixedRate={fixedRateLabel || undefined}
          extras={vehicleExtras || undefined}
        />
      )}
    </>
  );
};
